import { __awaiter } from '../../../_virtual/_tslib.js';
import { Connection, PublicKey } from '@solana/web3.js';
import { ProviderChain } from '@dynamic-labs/rpc-providers';
import { DynamicError } from '@dynamic-labs/utils';
import { MagicWalletConnectorBase } from '../../MagicWalletConnectorBase.js';
import { logger } from '../../utils/logger/logger.js';
import { SolanaMagicClient } from './SolanaMagicClient.js';

const GENESIS_HASHES = {
    '4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z': 'testnet',
    '5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp': 'mainnet',
    EtWTRABZaYq6iMfeYKouRu166VU2xqa1: 'devnet',
};
class MagicSolanaWalletConnector extends MagicWalletConnectorBase {
    constructor(props) {
        var _a;
        super(props);
        this.supportedChains = ['SOL'];
        this.connectedChain = 'SOL';
        this.solNetworks = props.solNetworks;
        this.chainRpcProviders = props.chainRpcProviders;
        (_a = this.chainRpcProviders) === null || _a === void 0 ? void 0 : _a.registerChainProviders(ProviderChain.SOLANA, (config) => {
            const rpcProviders = {};
            if (config === null || config === void 0 ? void 0 : config.solana) {
                rpcProviders.solana = config.solana.map((network) => {
                    var _a;
                    const rpcUrl = ((_a = network.privateCustomerRpcUrls) === null || _a === void 0 ? void 0 : _a[0]) || network.rpcUrls[0];
                    const provider = new Connection(rpcUrl);
                    return {
                        chainId: network.chainId.toString(),
                        chainName: network.name,
                        provider,
                    };
                });
            }
            return rpcProviders.solana;
        });
        this.magicClient = new SolanaMagicClient({
            apiKey: this.providerProps.apiKey,
            rpcUrl: this.getWalletClient().rpcEndpoint,
        });
    }
    filter() {
        const result = super.filter() && this.chain === 'SOL';
        if (!result)
            logger.warn(`MagicSolanaWalletConnector: Magic is configured for a different chain: ${this.chain}. This wallet connector will be disabled.`);
        return result;
    }
    getNetwork() {
        return __awaiter(this, void 0, void 0, function* () {
            const provider = this.getWalletClient();
            let genesisHash = yield provider.getGenesisHash();
            genesisHash = genesisHash.substring(0, 32);
            const network = GENESIS_HASHES[genesisHash];
            if (network) {
                return network;
            }
            else {
                return 'testnet';
            }
        });
    }
    getWalletClient() {
        var _a;
        const [network] = this.solNetworks;
        if (!network) {
            throw new DynamicError('No enabled networks');
        }
        const rpcUrl = ((_a = network.privateCustomerRpcUrls) === null || _a === void 0 ? void 0 : _a[0]) || network.rpcUrls[0];
        return new Connection(rpcUrl, 'confirmed');
    }
    getPublicClient() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (this.solNetworks.length === 0)
                return;
            const configurations = {
                cosmos: [],
                evm: undefined,
                solana: this.solNetworks,
                starknet: undefined,
            };
            return (_b = (_a = this.chainRpcProviders) === null || _a === void 0 ? void 0 : _a.getSolanaRpcProviderByChainId(configurations, '101')) === null || _b === void 0 ? void 0 : _b.provider;
        });
    }
    getSigner() {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error('getSigner: Method not implemented.');
        });
    }
    signMessage(messageToSign) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const result = yield this.walletUiUtils.signMessage({
                    handler: () => __awaiter(this, void 0, void 0, function* () {
                        const message = new TextEncoder().encode(messageToSign);
                        const signedMessage = yield this.magicClient
                            .getClient()
                            .solana.signMessage(message);
                        return Buffer.from(signedMessage).toString('base64');
                    }),
                    message: messageToSign,
                });
                return result;
            }
            catch (e) {
                logger.error(e);
                return undefined;
            }
        });
    }
    getBalance() {
        return __awaiter(this, void 0, void 0, function* () {
            const user = yield this.getUserMetadata();
            if (!(user === null || user === void 0 ? void 0 : user.publicAddress)) {
                return undefined;
            }
            const publicKey = new PublicKey(user.publicAddress);
            const balance = this.lamportsToSol(yield this.getWalletClient().getBalance(publicKey));
            return balance.toString();
        });
    }
    // Solana uses lamports as the smallest unit of currency. This converts lamports to SOL.
    lamportsToSol(lamports) {
        return lamports / 1000000000;
    }
}

export { MagicSolanaWalletConnector };
